// The shared table's rows in motion. A reorder or a small change glides every line to its new
// place, a row that leaves dissolves where it lay before the rest close up, and focus on a
// leaving row moves to its neighbour. A set change (a page turned, a view switched, most rows
// new, a very large pile) just renders. docs/features/shared-table.md.

import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { Exiting, MS, offerFocus, reducedMotion, useExiting } from '../ui/motion';
import { ROW_GLIDE, ROW_STILL } from './tableFace';

/** Past this many rows on either side of a change the table just renders: a glide this size is noise, and its layers cost. */
const FLIP_LIMIT = 200;
/** A change of up to this many rows always counts as small, so a short table still moves. The landing party's size. */
const FEW = 3;
/** Lines further than this outside the window jump instead of gliding: nobody watches them travel. */
const OFFSCREEN = 160;

/** Key prefixes for the body lines that are not rows. They glide with the rows; focus never lands on them. */
export const BAND_KEY = 'band:';
export const SUB_KEY = 'sub:';

export interface MotionLine {
  key: string;
  kind: 'band' | 'row';
}

const NONE: ReadonlySet<string> = new Set();
const keyOf = (line: MotionLine) => line.key;

const keyedLines = (body: HTMLElement): HTMLElement[] =>
  Array.from(body.children).filter((el): el is HTMLElement => el instanceof HTMLElement && !!el.dataset.lineKey);

const isRow = (el: HTMLElement) => {
  const key = el.dataset.lineKey || '';
  return !key.startsWith(BAND_KEY) && !key.startsWith(SUB_KEY);
};

const isLeaving = (el: Element) => el.getAttribute('aria-hidden') === 'true';

/** Ids that are just positions ("0", "1", ...): a removal would fade the last row, not the one removed. */
const byPosition = (keys: Iterable<string>): boolean => {
  let i = 0;
  for (const k of keys) {
    if (k !== String(i)) return false;
    i += 1;
  }
  return true;
};

/** A change small enough to move: most rows stay, the pile is not huge, and ids name rows. */
function isSmallChange(prev: ReadonlySet<string>, next: readonly string[]): boolean {
  if (prev.size === 0 || next.length === 0) return false;
  if (byPosition(prev) && byPosition(next)) return false;
  if (Math.max(prev.size, next.length) > FLIP_LIMIT) return false;
  let kept = 0;
  next.forEach((k) => {
    if (prev.has(k)) kept += 1;
  });
  const room = Math.max(kept, FEW);
  return kept > 0 && next.length - kept <= room && prev.size - kept <= room;
}

/**
 * Where each line stands in the window. Window coordinates on purpose: the snapshot is taken just
 * before the change commits, so nothing scrolls in between except the browser's own scroll
 * anchoring answering the change, and a row it holds still on screen must not glide.
 */
function measure(body: HTMLElement): Map<string, number> {
  const out = new Map<string, number>();
  keyedLines(body).forEach((el) => out.set(el.dataset.lineKey as string, el.getBoundingClientRect().top));
  return out;
}

/**
 * A line something else is moving: the PO lines' drag (Procurement/LineOrder) writes its own
 * transform and transition straight onto the row. Such a line is never measured, moved or cleared.
 */
const foreign = (el: HTMLElement, written: Map<HTMLElement, string>) =>
  !!el.style.transform || (!!el.style.transition && written.get(el) !== el.style.transition);

/**
 * FLIP on the `translate` property, never `transform`: the landing animation and a drag both own
 * the row's transform, and translate composes with either instead of overwriting it.
 */
function glide(body: HTMLElement, first: Map<string, number>, written: Map<HTMLElement, string>): boolean {
  const movers = keyedLines(body).filter((el) => first.has(el.dataset.lineKey as string) && !foreign(el, written));
  // One write pass, one read pass, one write pass: the browser lays out once, not once per row.
  movers.forEach((el) => {
    el.style.transition = ROW_STILL;
    el.style.translate = '';
    written.delete(el);
  });
  const tops = movers.map((el) => el.getBoundingClientRect().top);
  const reach = window.innerHeight + OFFSCREEN;
  const seen = (top: number) => top > -OFFSCREEN && top < reach;
  const moved = movers.filter((el, i) => {
    const was = first.get(el.dataset.lineKey as string) as number;
    const dy = was - tops[i];
    if (Math.abs(dy) < 0.5 || !(seen(was) || seen(tops[i]))) return false;
    el.style.translate = `0 ${dy}px`;
    return true;
  });
  // Commit the inverted places before releasing them, or the browser skips straight to the end.
  if (moved.length) void body.offsetHeight;
  movers.forEach((el) => {
    el.style.transition = '';
  });
  moved.forEach((el) => {
    el.style.transition = ROW_GLIDE;
    written.set(el, el.style.transition);
    el.style.translate = '';
  });
  // Start the glides inside this commit. A transition written onto a row later in the frame still
  // cancels its glide: the PO lines' grip (Procurement/LineOrder) does on an arrow-key step, so
  // some of those rows snap, as they did before glides existed.
  if (moved.length) void body.offsetHeight;
  return moved.length > 0;
}

function settle(written: Map<HTMLElement, string>) {
  written.forEach((t, el) => {
    if (el.isConnected && el.style.transition === t) el.style.transition = '';
  });
  written.clear();
}

/**
 * A row that starts leaving while it holds focus hands it on, as useFocusHandoff does for a list:
 * to the nearest live row after it, else before it, on a key that row marks `data-handoff` (a
 * focusable row marks itself). Rows leaving too are stepped over; group bands and opened details
 * are not rows and are passed by. With nothing marked, focus stays and falls to the page when the
 * row goes.
 */
function handFocusOn(body: HTMLElement): void {
  const active = document.activeElement;
  if (!active || !body.contains(active)) return;
  let line: Element | null = active;
  while (line && line.parentElement !== body) line = line.parentElement;
  if (!(line instanceof HTMLElement) || !isLeaving(line)) return;
  const lines = keyedLines(body);
  const key = line.dataset.lineKey || '';
  // Focus in an opened detail leaves with the row the detail opens from.
  const from = key.startsWith(SUB_KEY) ? lines.find((el) => el.dataset.lineKey === key.slice(SUB_KEY.length)) : line;
  if (!from || !isRow(from) || !isLeaving(from)) return;
  const rows = lines.filter(isRow);
  const at = rows.indexOf(from);
  for (const step of [1, -1]) {
    for (let i = at + step; i >= 0 && i < rows.length; i += step) {
      if (isLeaving(rows[i])) continue;
      const offered = offerFocus(rows[i]);
      if (offered === 'landed') return;
      if (offered === 'stop') break;
    }
  }
}

/**
 * The lines to draw, leaving ones included, and the rows that arrived in a small change (each
 * gets a fade unless it is part of the landing party). Every drawn `tr` in `bodyRef` carries
 * `data-line-key`: a row its id, a band `band:<key>`, an opened detail `sub:<row id>`, and a
 * leaving line is `aria-hidden`.
 */
export function useRowMotion<L extends MotionLine>(
  lines: L[],
  bodyRef: RefObject<HTMLTableSectionElement>,
): { shown: Exiting<L>[]; entering: ReadonlySet<string> } {
  // Latched per change of the row keys, order included: a re-render that hands over a fresh array
  // of the same rows keeps the last answer, so it can never cut an exit short.
  const latch = useRef<{ sig: string | null; keys: ReadonlySet<string>; moves: boolean; entering: ReadonlySet<string> }>({
    sig: null,
    keys: NONE,
    moves: false,
    entering: NONE,
  });
  const keys = lines.filter((l) => l.kind === 'row').map(keyOf);
  const rowSig = keys.join('\n');
  if (rowSig !== latch.current.sig) {
    const prev = latch.current.keys;
    const body = bodyRef.current;
    // jsdom lays nothing out, so a table under test never moves unless the test gives its body a
    // box. Neither does a table that is not laid out (a hidden tab): there is nothing to watch.
    const moves = isSmallChange(prev, keys) && !!body && body.getBoundingClientRect().height > 0;
    latch.current = {
      sig: rowSig,
      keys: new Set(keys),
      moves,
      entering: moves ? new Set(keys.filter((k) => !prev.has(k))) : NONE,
    };
  }
  const { moves, entering } = latch.current;
  const shown = useExiting<L>(lines, keyOf, MS.exit, moves);

  const drawnSig = shown.map((s) => (s.leaving ? `${s.key}~` : s.key)).join('\n');
  const committed = useRef<string | null>(null);
  const first = useRef<Map<string, number> | null>(null);
  if (drawnSig !== committed.current) {
    const body = bodyRef.current;
    // Read during render, before React moves anything: the lines still stand where they were
    // drawn, mid-glide included. Places recorded at the last change (useFlip's way) go stale the
    // moment a detail opens or a row grows in between.
    first.current = moves && committed.current !== null && body && !reducedMotion() ? measure(body) : null;
  }

  const written = useRef(new Map<HTMLElement, string>());
  const timer = useRef<number | undefined>(undefined);
  useLayoutEffect(() => {
    committed.current = drawnSig;
    const before = first.current;
    first.current = null;
    const body = bodyRef.current;
    if (!body) return;
    handFocusOn(body);
    if (!before || !glide(body, before, written.current)) return;
    window.clearTimeout(timer.current);
    // The glide's transition comes off once it has run, so the row's own class transitions are back.
    timer.current = window.setTimeout(() => settle(written.current), MS.spring + 40);
  }, [drawnSig, bodyRef]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { shown, entering };
}
